import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { TmDetailedError } from '@treasury/domain/shared';
import { CreditorsService, WireCreditor, WireIsoService } from '@treasury/domain/wires';
// eslint-disable-next-line no-duplicate-imports
import type { WireIsoLabels } from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../../services/jhd-header-bar.service';
import '../partials/creditor-confirm-step';
import '../partials/creditor-create-step';
import '../partials/creditor-review-step';

enum Step {
    Create,
    Edit,
    Review,
    Confirm,
}

export const tagName = 'creditor-workflow-container';
@customElement(tagName)
export class CreditorWorkflowContainer extends TmContainer {
    @InjectProperty()
    declare private creditorsService: CreditorsService;

    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @state()
    private creditor: WireCreditor = new WireCreditor();

    @state()
    private activeStep = Step.Create;

    @state()
    private creditorError?: TmDetailedError;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    @state()
    private params: { action: string; id: number } = { action: 'create', id: 0 };

    public connectedCallback(): void {
        super.connectedCallback();
        this.loading = false;
    }

    protected render() {
        return html`<tm-body class="h-full" .shadow=${false} .padding=${false}>
            ${this.loading ? this.renderBlockingLoader() : this.renderStep()}</tm-body
        >`;
    }

    protected async firstUpdated() {
        const { params } = await this.navService.getRouteData<{ action: string; id: number }>();
        this.params = params;
        this.wireIsoLabels = await this.wireIsoService.getLabels();
        if (this.params.action === 'edit') {
            this.creditor = await this.creditorsService.getById(this.params.id);
            this.activeStep = Step.Edit;
        }
    }

    private renderStep() {
        switch (this.activeStep) {
            case Step.Create:
                this.headerService.configure({
                    title: `Create ${this.wireIsoLabels.creditor}`,
                    backAction: () => this.navService.navigate('/wires/creditors'),
                });
                return html`<creditor-create-step
                    .creditor=${this.creditor}
                    @review=${({ detail }: CustomEvent) => {
                        this.creditor = detail;
                        this.activeStep = Step.Review;
                    }}
                ></creditor-create-step>`;
            case Step.Edit:
                this.headerService.configure({
                    title: `Edit ${this.wireIsoLabels.creditor}`,
                    backAction: () =>
                        this.navService.navigate(`/wires/creditors/view/${this.params.id}`),
                });
                return html`<creditor-create-step
                    .creditor=${this.creditor}
                    @review=${({ detail }: CustomEvent) => {
                        this.creditor = detail;
                        this.activeStep = Step.Review;
                    }}
                ></creditor-create-step>`;
            case Step.Review:
                this.headerService.configure({
                    title: `${this.wireIsoLabels.creditor} Detail`,
                    backAction: () => {
                        this.activeStep = Step.Create;
                    },
                });
                return html`<creditor-review-step
                    .creditor=${this.creditor}
                    .workflow=${this.params.action === 'edit' ? 'creditor-edit' : 'creditor-create'}
                    @create=${({ detail }: CustomEvent) => {
                        this.creditor = detail;
                        this.createCreditor();
                    }}
                    @edit=${({ detail }: CustomEvent) => {
                        this.creditor = detail;
                        this.updateCreditor();
                    }}
                ></creditor-review-step>`;
            case Step.Confirm:
                this.headerService.configure({
                    title: `${this.wireIsoLabels.creditor} Confirmation`,
                });
                return html`<creditor-confirm-step
                    .creditor=${this.creditor}
                    @create-creditor=${() => {
                        this.creditor = new WireCreditor();
                        this.activeStep = Step.Create;
                    }}
                    @view-creditors=${() => {
                        this.navService.navigate('/wires/creditors');
                    }}
                ></creditor-confirm-step>`;
            default:
                throw new Error(`Cannot render step. Invalid step: "${this.activeStep}".`);
        }
    }

    private renderBlockingLoader() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return nothing;
    }

    private async createCreditor() {
        this.loading = true;
        this.creditorError = undefined;
        try {
            const response = await this.creditorsService.create(this.creditor);
            this.creditor = response as WireCreditor;
            this.activeStep = Step.Confirm;
        } catch (error) {
            if (error instanceof TmDetailedError) {
                this.creditorError = error;
                this.notificationService.renderError(error);
            } else if (error instanceof Error) {
                this.notificationService.renderError(error);
            } else {
                this.notificationService.renderError('Error processing your wire');
            }
        } finally {
            this.loading = false;
        }
    }

    private async updateCreditor() {
        this.loading = true;
        this.creditorError = undefined;
        try {
            const response = await this.creditorsService.update(this.creditor);
            this.creditor = response as WireCreditor;
            this.activeStep = Step.Confirm;
        } catch (error) {
            if (error instanceof TmDetailedError) {
                this.creditorError = error;
                this.notificationService.renderError(error);
            } else if (error instanceof Error) {
                this.notificationService.renderError(error);
            } else {
                this.notificationService.renderError('Error processing your wire');
            }
        } finally {
            this.loading = false;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CreditorWorkflowContainer;
    }
}
